import { shouldDisableSessionRecording } from './profileUtils';
import type { PostHogConfig } from 'posthog-js';

// Define the environment variables used by PostHog
interface PostHogEnv {
  VITE_PUBLIC_POSTHOG_KEY: string;
  VITE_PUBLIC_POSTHOG_HOST: string;
  VITE_POSTHOG_DEV_ENABLED?: string;
  MODE: string;
}

/**
 * Generates the PostHog configuration object based on the current environment mode.
 * @param env The VITE environment variables (import.meta.env)
 * @param sessionRecordingConsent Whether user has consented to session recording (default: false for initial config)
 * @returns The PostHog configuration object (PostHogConfig)
 */
export function getPostHogConfig(
  env: PostHogEnv,
  sessionRecordingConsent = false,
): PostHogConfig {
  const mode = env.MODE;

  // Core Configuration (applies to all enabled environments)
  // Fallback api_host when HOST is unset (e.g. only KEY in env) so events can reach PostHog
  const baseConfig: Partial<PostHogConfig> = {
    api_host: env.VITE_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
    defaults: '2025-05-24',
    capture_exceptions: true, // Essential for error logging
    autocapture: false, // Essential for controlled logging
    cookieless_mode: 'on_reject',
    // Mask sensitive data in session recordings (COPPA/GDPR-K compliance)
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '.sensitive-data, .ph-no-capture',
    },
  };

  // Determine initial session recording state
  // In production: respect user consent (default disabled until consent given)
  // In staging/dev: always disabled
  const initialRecordingDisabled = mode !== 'production' || !sessionRecordingConsent;

  // Environment-specific configuration
  return {
    ...(baseConfig as PostHogConfig),
    // Initial session recording state based on environment and consent
    disable_session_recording: initialRecordingDisabled,
    // Enable debug logging in staging and development
    debug: mode !== 'production',

    // Environment-Specific Logic
    // Using any type because PostHogInterface doesn't include set_config but PostHog does
     
    loaded: function (ph: any) {
      if (mode === 'production') {
        // In production, respect user consent + profile age
        // Will be dynamically updated based on active profile
        ph.set_config({ disable_session_recording: !sessionRecordingConsent });
        console.log(
          `PostHog loaded in Production (Session Recording ${sessionRecordingConsent ? 'Enabled' : 'Disabled'}).`,
        );
      }
    },
  };
}

/**
 * Update PostHog session recording state dynamically based on consent and active profile
 * @param posthog PostHog instance
 * @param userConsent Whether user has consented to session recording
 * @param activeProfile Active profile with optional ageRange (Convex schema uses camelCase)
 */
export function updateSessionRecordingState(
   
  posthog: any,
  userConsent: boolean,
  activeProfile?: { ageRange?: string | null },
): void {
  if (!posthog) {
    return;
  }

  const shouldDisable = shouldDisableSessionRecording(userConsent, activeProfile);

  posthog.set_config({ disable_session_recording: shouldDisable });

  console.log(
    `PostHog: Session recording ${shouldDisable ? 'disabled' : 'enabled'} ` +
      `(consent: ${userConsent}, child profile: ${activeProfile?.ageRange && shouldDisableSessionRecording(true, activeProfile)})`,
  );
}

/**
 * Checks if PostHog should be initialized in the current environment mode.
 * Initializes in production by default, or in development if VITE_POSTHOG_DEV_ENABLED is set to 'true'.
 * @param mode The current environment mode.
 * @param devEnabled Optional flag to enable PostHog in development (from VITE_POSTHOG_DEV_ENABLED env var).
 * @returns True if PostHog should be initialized, false otherwise.
 */
export function shouldInitializePostHog(mode: string, devEnabled?: string): boolean {
  if (mode === 'production') {
    return true;
  }
  
  // Allow opt-in for development via environment variable
  return devEnabled === 'true';
}
